// ===========================
// CONFIGURATION CONSTANTS
// ===========================

const CONFIG = {
  spayedFactor: {
    yes: 1,
    no: 1.1
  },
  
  caloriesFactor: {
    cat: { junior: 109.3, adult: 109.4, senior: 109.4 },
    dog: { junior: 113, adult: 85.4, senior: 85.4 }
  },
  
  activityFactor: {
    low: 1.2,
    medium: 1.4,
    high: 1.6
  },
  
  bodyConditionFactor: {
    1: 1.2,
    2: 1.1,
    3: 1,
    4: 0.9,
    5: 0.8
  },
  
  weightRanges: {
    dog: [
      { maxAge: 1, min: 2, max: 10 },
      { maxAge: 3, min: 10, max: 25 },
      { maxAge: 7, min: 20, max: 35 },
      { maxAge: 12, min: 18, max: 30 }
    ],
    cat: [
      { maxAge: 1, min: 1, max: 3 },
      { maxAge: 3, min: 3, max: 5 },
      { maxAge: 7, min: 4, max: 6 },
      { maxAge: 12, min: 3, max: 5 }
    ]
  },
  
  basicCalorieFactor: { cat: 70, dog: 70 }
};

// ===========================
// STATE MANAGEMENT
// ===========================

const state = {
  petDetails: {},
  productRecommendations: [],
  calculationDetail: {}
};

// ===========================
// DOM UTILITIES
// ===========================

const DOM = {
  get: (selector) => document.querySelector(selector),
  getAll: (selector) => document.querySelectorAll(selector),
  
  getCheckedValue: (name) => {
    const elements = DOM.getAll(`[name="${name}"]`);
    for (const el of elements) {
      if (el.checked) return el.value;
    }
    return null;
  },
  
  getCheckedValues: (name) => {
    const elements = DOM.getAll(`[name="${name}"]`);
    return Array.from(elements)
      .filter(el => el.checked)
      .map(el => el.value);
  },
  
  toggleClass: (element, ...classes) => {
    if (typeof element === 'string') {
      element = DOM.get(element);
    }
    element?.classList.toggle(...classes);
  },
  
  addClass: (element, ...classes) => {
    if (typeof element === 'string') {
      element = DOM.get(element);
    }
    element?.classList.add(...classes);
  },
  
  removeClass: (element, ...classes) => {
    if (typeof element === 'string') {
      element = DOM.get(element);
    }
    element?.classList.remove(...classes);
  }
};

// ===========================
// CALCULATION UTILITIES
// ===========================

const Calculator = {
  roundToHalf: (num) => Math.round(num * 2) / 2,
  
  getAgeType: (age) => {
    if (age <= 0) return 'junior';
    if (age <= 8) return 'adult';
    return 'senior';
  },
  
  getInitialCalories: (species, ageType, weight) => {
    const basicCalories = CONFIG.basicCalorieFactor[species] * Math.pow(weight, 0.75);
    return ageType === 'junior' ? basicCalories * 2 : basicCalories;
  },
  
  calculateCalories: () => {
    const species = DOM.getCheckedValue('species') || 'dog';
    const weight = parseFloat(DOM.get('[name="weight"]').value);
    const age = parseInt(DOM.get('[name="age"]').value);
    const neutered = DOM.get('[name="neutered"]').value;
    const bodyCondition = DOM.getCheckedValue('body-condition') || '0';
    const activity = DOM.getCheckedValue('activity') || '0';
    
    const ageType = Calculator.getAgeType(age);
    const portion = parseInt(DOM.get('[name="portion"]').value)/100;
    const foodType = DOM.get('[name="food_type"]').value;
    const otherFoodCalories = DOM.get('[name="calories"]').value / 1000;
    
    let calories = Calculator.getInitialCalories(species, ageType, weight);
    calories *= CONFIG.spayedFactor[neutered];
    calories *= CONFIG.bodyConditionFactor[bodyCondition];
    
    if (species === 'dog') {
      calories *= CONFIG.activityFactor[activity];
    }
    
    const caloriesFactor = CONFIG.caloriesFactor[species][ageType];
    const totalCalories = Math.round(calories);
    const petcubesCalories = totalCalories * portion;
    console.log(totalCalories, petcubesCalories, portion);
    const cubes = species === 'dog'
      ? Math.round(petcubesCalories / caloriesFactor)
      : Calculator.roundToHalf(calories / caloriesFactor);
    
    return { calories: Math.round(calories), cubes, weight };
  },
  
  updateDisplay: (results) => {
    const { calories, cubes, weight } = results;
    const cubeUnit = cubes > 1 ? window.T.cubePlural : window.T.cubeSingular;
    
    DOM.get('.current-weight .value').innerHTML = `${weight}kg`;
    DOM.get('.calories .value').innerHTML = `${calories}kcal`;
    DOM.get('.cubes .value').innerHTML = `${cubes} ${cubeUnit}`;
    DOM.get('.result-title').innerHTML = `${window.T.resultTitlePrefix} ${DOM.get('[name="petname"]').value}!`;
    
    state.calculationDetail = {
      currentValue: `${weight}kg`,
      caloriesValue: `${calories}kcal`,
      cubesValue: `${cubes} ${cubeUnit}`,
      otherFood: DOM.get('#food-type').value,
      otherFoodCalories: DOM.get('#calories').value
    };
  }
};

// ===========================
// FORM VALIDATION
// ===========================

const Validation = {
  email: (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },
  
  form: () => {
    const requiredFields = ['petname', 'age', 'weight', 'neutered'];
    let isValid = true;
    
    requiredFields.forEach(fieldName => {
      const input = DOM.get(`[name="${fieldName}"]`);
      const label = DOM.get(`.${fieldName}-label`);
      
      if (!input.value) {
        DOM.addClass(input, 'error');
        DOM.addClass(label, 'error');
        isValid = false;
      } else {
        DOM.removeClass(input, 'error');
        DOM.removeClass(label, 'error');
      }
    });
    
    return isValid;
  }
};

// ===========================
// PRODUCT MANAGEMENT
// ===========================

const Products = {
  async fetch(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  },
  
  filter(data, excludedIngredients) {
    const age = parseInt(DOM.get('[name="age"]').value);
    const ageType = Calculator.getAgeType(age);
    let products = data.products[ageType];
    
    if (!excludedIngredients?.length) return products;
    
    const excluded = excludedIngredients.map(ing => ing.toLowerCase());
    return products.filter(product => {
      const ingredients = product.ingredients.map(ing => ing.toLowerCase());
      return !ingredients.some(ing => excluded.includes(ing));
    });
  },
  
  display(products, containerId = 'product-container') {
    const container = DOM.get(`.${containerId}`);
    if (!container) return;
    
    container.innerHTML = '';
    const limited = products.slice(0, 3);
    state.productRecommendations = limited;
    
    limited.forEach(product => {
      const card = document.createElement('div');
      card.className = 'product-card col-6 col-lg-4';
      card.innerHTML = `
        <img src="${product.image}" alt="${product.name}">
        <h3>${product.name}</h3>
      `;
      container.appendChild(card);
    });
  },
  
  async initialize(jsonUrl, excludedIngredients = null) {
    const data = await Products.fetch(jsonUrl);
    if (data) {
      const filtered = Products.filter(data, excludedIngredients);
      Products.display(filtered);
    } else {
      console.error('Failed to load product data');
    }
  }
};

// ===========================
// UI ACTIONS
// ===========================

const UI = {
  toggleSpecies(species) {
    const body = DOM.get('body');
    const petDetail = DOM.get('.pet-detail');
    const petDetailWrapper = DOM.get('.pet-detail-wrapper');
    
    DOM.getAll('[data-toggle="species"]').forEach(el => {
      if (el.hasAttribute('src')) {
        el.src = el.dataset[species];
      } else {
        el.innerHTML = el.dataset[species];
      }
    });
    
    DOM.removeClass(petDetail, 'dog', 'cat');
    DOM.addClass(petDetail, species);
    DOM.removeClass(body, 'dog', 'cat');
    DOM.addClass(body, species);
    DOM.addClass(petDetailWrapper, species);
  },
  
  resetAllergies() {
    DOM.getAll('[name="allergies[]"]').forEach(el => {
      if (el.value !== 'none') el.checked = false;
    });
  },
  
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
  
  lockBody() {
    const body = DOM.get('.body');
    DOM.addClass(body, 'overflow-hidden', 'position-fixed');
  },
  
  unlockBody() {
    const body = DOM.get('.body');
    DOM.removeClass(body, 'overflow-hidden', 'position-fixed');
  },
  
  showModal(type) {
    const overlay = DOM.get('.confirmation-overlay');
    const modal = DOM.get(`.confirmation-${type}`);
    
    DOM.removeClass(overlay, 'd-none');
    DOM.removeClass(modal, 'd-none');
    UI.lockBody();
  },
  
  hideModal(type) {
    const overlay = DOM.get('.confirmation-overlay');
    const modal = DOM.get(`.confirmation-${type}`);
    
    DOM.addClass(overlay, 'd-none');
    DOM.addClass(modal, 'd-none');
    UI.unlockBody();
  },
  
  resetForm() {
    UI.toggleSpecies('dog');
    UI.resetAllergies();
    DOM.get('.main-form').reset();
    UI.scrollToTop();
  },
  
  switchToResults() {
    DOM.addClass('#calculator', 'd-none');
    DOM.removeClass('#results', 'd-none');
  },
  
  switchToCalculator() {
    DOM.removeClass('#calculator', 'd-none');
    DOM.addClass('#results', 'd-none');
  }
};

// ===========================
// PRINT & EMAIL
// ===========================

const Output = {
  generateProductTable(products) {
    const columnWidth = Math.floor(100 / 3);
    const cells = products.map(product => 
      `<td width="${columnWidth}%" align="center" style="padding:5px;vertical-align:top;">
        <img src="${product.image}" alt="${product.name}" width="128" height="128" style="max-width:100%;height:auto;margin-bottom:-12px">
        <p style="color:#666;font-size:14px;line-height:1.3;margin:10px 0">${product.name}</p>
      </td>`
    ).join('');
    
    return `<table border="0" cellpadding="0" cellspacing="0" width="100%"><tr>${cells}</tr></table>`;
  },
  
  generatePetDetailsTable(petData) {
    const mapping = window.T.petDetailsMapping;
    return Object.entries(mapping)
      .map(([key, label]) => 
        `<tr>
          <td style="color:#666;font-weight:bold;font-size:12px;line-height:16px;padding:2px 0">
            <span style="display:inline-block;width:140px">${label}</span>
          </td>
          <td style="text-transform:capitalize;font-size:12px;">: ${petData[key]}</td>
        </tr>`
      ).join('');
  },
  
  print() {
    const species = DOM.getCheckedValue('species');
    const petName = state.petDetails.petname;
    const petImage = `https://www.petcubes.com/cdn/shop/t/115/assets/top-${species}.png`;
    const productTable = Output.generateProductTable(state.productRecommendations);
    const petDetailHTML = Output.generatePetDetailsTable(state.petDetails);
    
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${window.T.printTableTitle}</title><link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap" rel="stylesheet"><link href="https://fonts.cdnfonts.com/css/more-sugar" rel="stylesheet"><style>body{margin:0;padding:0;background-color:#fff;font-family:Poppins,system,sans-serif}@page{size:A5 portrait;margin:0}@media print{.print-container{transform:scale(0.90);width:148mm;height:210mm;margin:0 auto;page-break-after:avoid}}</style></head><body><div class="print-container"><table border="0" cellpadding="0" cellspacing="0" width="85%" style="max-width:600px;margin:0 auto"><tr><td style="padding:10px"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td width="48"><img src="https://www.petcubes.com/cdn/shop/t/115/assets/petcubes-logo-2.png?v=148794154900949279151741570581" alt="Pet Cubes" width="48" height="48" style="display:block"></td><td style="padding-left:15px"><h2 style="color:#29b589;font-size:21px;line-height:48px;font-weight:500;font-family:'More Sugar',system,sans-serif">${window.T.printTableTitle}</h2></td></tr></table><table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width:600px;margin:0 auto"><tr><td style="padding:10px"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td width="100%" style="text-align:center"><img src="${petImage}" alt="Pet Species" width="auto" height="96" style="margin:0 auto;margin-top:-16px;"></td></tr></table><table border="0" cellpadding="0" cellspacing="0" width="100%" bgcolor="D9EFEA" style="margin-top:0;border-radius:8px"><tr><td style="padding:10px"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td style="color:#29b589;font-size:18px;padding-bottom:10px"><h3 style="margin:0;font-weight:500;font-family:'More Sugar',system,sans-serif">${window.T.printTableDetails}</h3></td></tr>${petDetailHTML}</table></td></tr></table><table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-top:6px"><tr><td><h3 style="color:#29b589;font-size:18px;padding-bottom:6px;margin:0;font-weight:500;line-height:1;font-family:'More Sugar',system,sans-serif">${window.T.printTablePlan} ${petName}!</h3></td></tr><tr><td style="color:#666;font-size:16px;padding-bottom:10px;font-weight:400;font-family:Poppins,system,sans-serif"><h4 style="margin:0;line-height:1.25;font-size:14px">${window.T.printTableRec}</h4></td></tr></table><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td width="33.33%" style="vertical-align:top"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td style="color:#666;font-weight:400;font-size:14px;font-family:'More Sugar',system,sans-serif"><strong>${window.T.printCurrent}:</strong></td></tr><tr><td style="color:#29b589;font-weight:400;font-size:18px;font-weight:700;font-family:'More Sugar',system,sans-serif">${state.calculationDetail.currentValue}</td></tr></table></td><td width="33.33%" style="vertical-align:top"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td style="color:#666;font-weight:400;font-size:14px;font-family:'More Sugar',system,sans-serif"><strong>${window.T.printRecommendedCalories}:</strong></td></tr><tr><td style="color:#29b589;font-weight:400;font-size:18px;font-weight:700;font-family:'More Sugar',system,sans-serif">${state.calculationDetail.caloriesValue}</td></tr></table></td><td width="33.33%" style="vertical-align:top"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tr><td style="color:#666;font-weight:400;font-size:14px;font-family:'More Sugar',system,sans-serif"><strong>${window.T.printPetCubes}:</strong></td></tr><tr><td style="color:#29b589;font-weight:400;font-size:18px;font-weight:700;font-family:'More Sugar',system,sans-serif">${state.calculationDetail.cubesValue}</td></tr></table></td></tr></table><table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-top:6px"><tr><td style="color:#666;font-weight:400;font-size:16px;padding-bottom:2px;font-family:'More Sugar',system,sans-serif"><strong>${window.T.printRecommendedProducts}:</strong></td></tr></table>${productTable}</td></tr></table></td></tr></table></div></body></html>`;
    
    const printWindow = window.open('', '');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    
    printWindow.onload = () => {
      const images = printWindow.document.images;
      let loadedCount = 0;
      const totalImages = images.length;
      
      const checkAndPrint = () => {
        if (loadedCount === totalImages) {
          printWindow.print();
          printWindow.close();
        }
      };
      
      if (totalImages === 0) {
        printWindow.print();
        printWindow.close();
        return;
      }
      
      Array.from(images).forEach(img => {
        if (img.complete) {
          loadedCount++;
          checkAndPrint();
        } else {
          img.onload = img.onerror = () => {
            loadedCount++;
            checkAndPrint();
          };
        }
      });
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 5000);
    };
  },
  
  async sendEmail() {
    const toAddress = DOM.get('[name="email"]').value;
    
    try {
      const response = await fetch('https://petcubes.hk/apps/email_send', {
        method: 'POST',
        body: JSON.stringify({
          toAddress,
          productRecommendations: state.productRecommendations,
          petDetails: state.petDetails,
          calculationDetail: state.calculationDetail
        })
      });
      
      return await response.text();
    } catch (error) {
      console.error('Email error:', error);
      throw error;
    }
  }
};

// ===========================
// EVENT HANDLERS
// ===========================

const Handlers = {
  species(e) {
    if (e.target.checked) {
      UI.toggleSpecies(e.target.value);
    }
  },
  
  allergies(e) {
    const allergies = DOM.getAll('[name="allergies[]"]');
    if (e.target.value === 'none') {
      UI.resetAllergies();
    } else {
      DOM.get('#allergy-none').checked = false;
    }
  },
  
  toggleDetail(e) {
    e.preventDefault();
    const petDetail = DOM.get('.pet-detail');
    const wrapper = DOM.get('.pet-detail-wrapper');
    
    if (petDetail.classList.contains('d-none')) {
      DOM.removeClass(petDetail, 'd-none');
      setTimeout(() => {
        DOM.removeClass(wrapper, 'enter');
        UI.lockBody();
      }, 250);
    } else {
      DOM.addClass(wrapper, 'enter');
      UI.unlockBody();
      setTimeout(() => {
        DOM.addClass(petDetail, 'd-none');
      }, 250);
    }
  },
  
  closeDetail() {
    const petDetail = DOM.get('.pet-detail');
    const wrapper = DOM.get('.pet-detail-wrapper');
    
    DOM.addClass(wrapper, 'enter');
    UI.unlockBody();
    setTimeout(() => {
      DOM.addClass(petDetail, 'd-none');
    }, 250);
  },
  
  async getResult(e) {
    e.preventDefault();
    
    if (!Validation.form()) {
      UI.scrollToTop();
      return;
    }
    
    const species = DOM.getCheckedValue('species') || 'dog';
    const jsonURL = `https://www.petcubes.com/cdn/shop/t/115/assets/${species}.json`;
    const allergies = DOM.getCheckedValues('allergies[]');
    
    const results = Calculator.calculateCalories();
    Calculator.updateDisplay(results);
    await Products.initialize(jsonURL, allergies);
    
    Handlers.updatePetDetails();
    Handlers.setupFinishButton();
    
    UI.scrollToTop();
    UI.switchToResults();
    
    if (window.innerWidth < 960) {
      UI.unlockBody();
    }
  },
  
  updatePetDetails() {
    const species = DOM.getCheckedValue('species') || 'dog';
    const values = DOM.getAll('.field-value');
    
    values.forEach(el => {
      const target = el.dataset.target;
      let value = '';
      
      switch (target) {
        case 'allergies':
          const allergies = DOM.getCheckedValues('allergies[]');
          value = allergies.includes('none') 
            ? window.T.option_none 
            : allergies.join(', ');
          break;
          
        case 'body-condition':
          const bcIndex = Array.from(DOM.getAll('[name="body-condition"]'))
            .findIndex(el => el.checked);
          value = window.T.bodyCondition[bcIndex];
          break;
          
        case 'activity':
          const actIndex = Array.from(DOM.getAll('[name="activity"]'))
            .findIndex(el => el.checked);
          value = window.T.activity[species][actIndex];
          break;
          
        case 'age':
          const ageIndex = parseInt(DOM.get('[name="age"]').value);
          value = window.T.ageOptions[ageIndex];
          break;
          
        case 'weight':
          value = DOM.get('[name="weight"]').value + ' kg';
          break;
          
        case 'species':
          value = species;
          break;
          
        case 'neutered':
          const neutered = DOM.get('[name="neutered"]').value;
          value = neutered === 'yes' ? window.T.yes : window.T.no;
          break;
          
        default:
          value = DOM.get(`[name="${target}"]`)?.value || '';
      }
      
      el.innerHTML = value;
      state.petDetails[target] = value;
    });
  },
  
  setupFinishButton() {
    const finishButton = DOM.get('#finish');
    const newsletter = DOM.getAll('[name="newsletter"]');
    
    newsletter.forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.checked) {
          const toggle = e.target.value;
          DOM.removeClass(finishButton, 'd-none');
          
          if (toggle === 'no') {
            DOM.removeClass(finishButton, 'btn-green');
            DOM.addClass(finishButton, 'btn-grey');
          } else {
            DOM.addClass(finishButton, 'btn-green');
            DOM.removeClass(finishButton, 'btn-grey');
          }
          
          finishButton.innerHTML = finishButton.dataset[toggle];
        }
      });
    });
    
    finishButton.addEventListener('click', async (e) => {
      if (e.target.innerHTML === window.T.emailButtonClose) {
        UI.showModal('thank-you');
      } else {
        const email = DOM.get('[name="email"]');
        const emailLabel = DOM.get('.email-label');
        
        if (Validation.email(email.value)) {
          DOM.removeClass(email, 'error');
          DOM.removeClass(emailLabel, 'error');
          
          try {
            await Output.sendEmail();
            UI.showModal('email');
          } catch (error) {
            console.error('Email error:', error);
          }
        } else {
          DOM.addClass(email, 'error');
          DOM.addClass(emailLabel, 'error');
        }
      }
    });
  },
  
  reset(e) {
    e.preventDefault();
    UI.showModal('reset');
  },
  
  print() {
    Output.print();
  }
};

// ===========================
// MODAL HANDLERS
// ===========================

const Modals = {
  setupResetModal() {
    const yesBtn = DOM.get('.confirmation-reset .btn-green');
    const noBtn = DOM.get('.confirmation-reset .btn-grey');
    
    noBtn.addEventListener('click', () => {
      UI.hideModal('reset');
    });
    
    yesBtn.addEventListener('click', () => {
      UI.hideModal('reset');
      UI.resetForm();
    });
  },
  
  setupThankYouModal() {
    const okBtn = DOM.get('.confirmation-thank-you .btn-green');
    
    okBtn.addEventListener('click', () => {
      UI.hideModal('thank-you');
      UI.resetForm();
      UI.switchToCalculator();
    });
  },
  
  setupEmailModal() {
    const okBtn = DOM.get('.confirmation-email .btn-green');
    
    okBtn.addEventListener('click', () => {
      UI.hideModal('email');
      UI.resetForm();
      UI.switchToCalculator();
    });
  }
};

// ===========================
// INITIALIZATION
// ===========================

window.addEventListener('load', () => {
  // Species toggle
  DOM.getAll('[name="species"]').forEach(el => {
    el.addEventListener('click', Handlers.species);
  });
  
  // Allergies
  DOM.getAll('[name="allergies[]"]').forEach(el => {
    el.addEventListener('click', Handlers.allergies);
  });
  
  // Detail toggle
  DOM.get('#toggle-detail')?.addEventListener('click', Handlers.toggleDetail);
  DOM.get('.toggle-close')?.addEventListener('click', Handlers.closeDetail);
  
  // Main actions
  DOM.get('#get-result')?.addEventListener('click', Handlers.getResult);
  DOM.get('.btn-reset')?.addEventListener('click', Handlers.reset);
  DOM.get('#print')?.addEventListener('click', Handlers.print);
  
  // Setup modals
  Modals.setupResetModal();
  Modals.setupThankYouModal();
  Modals.setupEmailModal();
});